import React, { useState, useEffect } from 'react';

import { getEventosPaginados } from '../../http/Actions/EventosAction';
import { getSeccionesByEvento } from '../../http/Actions/SeccionEventoAction';
import { createTicketsBulk } from '../../http/Actions/TicketsAction';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import 'tailwindcss/tailwind.css';

const VentaEntradas = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [eventos, setEventos] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSections, setLoadingSections] = useState(false);
  const [processingPurchase, setProcessingPurchase] = useState(false);
  const [error, setError] = useState(null);
  
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // Función para extraer el oid del token
  const getUserIdFromToken = () => {
    try {
      if (!token) return null;
      const decoded = jwtDecode(token);
      return decoded.oid;
    } catch (error) {
      console.error('Error decodificando token:', error);
      return null;
    }
  };

  // Función para obtener el nombre del usuario del token
  const getUserNameFromToken = () => {
    try {
      if (!token) return null;
      const decoded = jwtDecode(token);
      return decoded.name || 'Usuario';
    } catch (error) {
      console.error('Error decodificando token:', error);
      return 'Usuario';
    }
  };

  // Cargar eventos del backend
  useEffect(() => {
    const fetchEventos = async () => {
      try {
        setLoading(true);
        await getEventosPaginados(
          (response) => {
            const eventosTransformados = response.data.data.map(evento => ({
              id: evento.eventoId,
              title: evento.titulo,
              comedian: evento.comediantes?.length > 0 
                ? evento.comediantes.filter(c => c.esPrincipal)[0]?.nombre || evento.comediantes[0]?.nombre
                : "Varios Artistas",
              date: new Date(evento.fecha).toLocaleDateString('es-CL', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              }),
              time: new Date(evento.fecha).toLocaleTimeString('es-CL', { 
                hour: '2-digit', 
                minute: '2-digit' 
              }),
              venue: evento.lugar?.nombre || "Por definir",
              location: evento.lugar ? `${evento.lugar.ciudad}, ${evento.lugar.region}` : "Chile",
              type: evento.lugar?.nombre?.toLowerCase().includes('bar') || 
                    evento.lugar?.nombre?.toLowerCase().includes('cervecería') ? 'bar' : 'teatro',
              description: evento.descripcion,
              duration: `${evento.duracionMinutos || 120} minutos`,
              image: evento.imagen,
              comediantes: evento.comediantes?.map(c => ({
                name: c.nombre,
                image: c.imagen,
                bio: c.biografia,
                esPrincipal: c.esPrincipal
              })) || [],
              venueImage: evento.lugar?.imagen || "https://images.unsplash.com/photo-1572116469696-31de0f17cc34?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
              venueAddress: evento.lugar?.direccion || "Dirección por confirmar",
              venueMetro: "Metro más cercano por confirmar",
              mapImage: evento.mapaLugar || "https://via.placeholder.com/800x300?text=Mapa+no+disponible"
            }));
            
            setEventos(eventosTransformados);
          },
          setError,
          1,
          50,
          "",
          token
        );
      } catch (err) {
        console.error("Error al cargar eventos:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEventos();
  }, [token]);

  // Cargar secciones cuando se selecciona un evento
  const handleSelectEvent = async (event) => {
    setSelectedEvent(event);
    setLoadingSections(true);
    setSelectedSection(null);
    setTicketQuantity(1);

    try {
      await getSeccionesByEvento(
        event.id,
        (secciones) => {
          const seccionesTransformadas = secciones.map(seccion => ({
            id: seccion.seccionEventoId,
            name: seccion.nombreSeccion,
            price: seccion.precio,
            available: seccion.entradasRestantes,
            details: seccion.descripcion || "Entrada general"
          }));
          setSecciones(seccionesTransformadas);
        },
        setError,
        token
      );
    } catch (err) {
      console.error("Error al cargar secciones:", err);
      const seccionesDefault = event.type === 'bar' ? [
        {
          id: "general",
          name: "Entrada General",
          price: 10000,
          available: 50,
          details: "Entrada general + 1 bebida"
        }
      ] : [
        {
          id: "general",
          name: "General",
          price: 15000,
          available: 100,
          details: "Entrada general"
        }
      ];
      setSecciones(seccionesDefault);
    } finally {
      setLoadingSections(false);
    }
  };

  // Función para procesar la compra
  const handlePurchase = async () => {
    const userId = getUserIdFromToken();
    const userName = getUserNameFromToken();
    
    if (!userId) {
      setError("No se pudo obtener la información del usuario. Por favor, inicia sesión nuevamente.");
      return;
    }

    const selectedSectionData = secciones.find(s => s.id === selectedSection);
    if (!selectedSectionData) {
      setError("Sección no encontrada");
      return;
    }

    setProcessingPurchase(true);
    setError(null);

    try {
      // Crear array de tickets para compra bulk
      const ticketsData = Array.from({ length: ticketQuantity }, () => ({
        eventoId: selectedEvent.id,
        usuarioId: userId,
        precio: selectedSectionData.price,
        seccionEventoId: selectedSectionData.id
      }));

      // Crear los tickets
      const result = await createTicketsBulk(ticketsData, setError, token);
      
      if (result && result.data) {
        // Redirigir a página de éxito con los datos
        navigate('/compra-exitosa', {
          state: {
            tickets: result.data,
            event: selectedEvent,
            section: selectedSectionData,
            quantity: ticketQuantity,
            total: selectedSectionData.price * ticketQuantity,
            userName: userName
          }
        });
      }
    } catch (error) {
      console.error('Error al procesar la compra:', error);
      setError('Hubo un error al procesar tu compra. Por favor, intenta nuevamente.');
    } finally {
      setProcessingPurchase(false);
    }
  };
  
  // Formato de moneda chilena
  const formatCLP = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  // Calcular el total
  const calculateTotal = () => {
    if (!selectedEvent || !selectedSection || secciones.length === 0) return 0;
    const section = secciones.find(s => s.id === selectedSection);
    return section ? section.price * ticketQuantity : 0;
  };
  
  // Volver a la lista de eventos
  const goBackToList = () => {
    setSelectedEvent(null);
    setSelectedSection(null);
    setTicketQuantity(1);
    setSecciones([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="bg-red-500/20 border border-red-500 text-red-100 px-6 py-4 rounded-lg max-w-md">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <HeroSection />
      {!selectedEvent ? (
        <EventsListSection 
          events={eventos} 
          onSelectEvent={handleSelectEvent} 
        />
      ) : (
        <ShowDetailsSection 
          event={selectedEvent} 
          secciones={secciones}
          loadingSections={loadingSections}
          selectedSection={selectedSection} 
          setSelectedSection={setSelectedSection} 
          ticketQuantity={ticketQuantity}
          setTicketQuantity={setTicketQuantity}
          onGoBack={goBackToList}
          formatCLP={formatCLP}
          calculateTotal={calculateTotal}
          onPurchase={handlePurchase}
          processingPurchase={processingPurchase}
        />
      )}
    </>
  );
};

const HeroSection = () => {
  return (
    <section className="relative py-16 md:py-24 w-full h-[40vh] min-h-[300px] overflow-hidden">
      <div className="absolute inset-0">
        <img 
          src="https://t3.ftcdn.net/jpg/08/28/48/08/360_F_828480810_czc0TwEaWqAHvzmwF9rlbQ6WX5bf4QyB.jpg" 
          alt="Comedy Events Background" 
          className="w-full h-full object-cover opacity-50" 
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
      </div>
      
      <div className="relative z-10 container mx-auto px-4 h-full flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Eventos de Comedia</h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            Los mejores shows de comedia en bares y teatros de Santiago
          </p>
        </div>
      </div>
    </section>
  );
};

const EventsListSection = ({ events, onSelectEvent }) => {
  return (
    <section className="py-16 bg-black text-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold mb-12 text-center">Próximos Eventos</h2>
        
        {events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {events.map((event) => (
              <EventCard 
                key={event.id} 
                event={event} 
                onClick={() => onSelectEvent(event)} 
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No hay eventos disponibles en este momento</p>
          </div>
        )}
      </div>
    </section>
  );
};

const EventCard = ({ event, onClick }) => {
  return (
    <div 
      className="bg-zinc-900 rounded-xl overflow-hidden transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl cursor-pointer"
      onClick={onClick}
    >
      <div className="relative h-48">
        <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
        <div className="absolute top-0 right-0 m-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            event.type === 'bar' 
              ? 'bg-red-600'
              : 'bg-purple-600'
          }`}>
            {event.type === 'bar' ? 'Bar' : 'Teatro'}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
          <h3 className="text-xl font-bold">{event.title}</h3>
          <p className="text-white/90">{event.comedian}</p>
        </div>
      </div>
      
      <div className="p-4">
        <div className="flex justify-between items-center">
          <div>
            <p className="font-medium">{event.date}</p>
            <p className="text-white/80">{event.time} hrs.</p>
          </div>
          <div className="text-right">
            <p className="font-medium">{event.venue}</p>
            <p className="text-white/80">{event.location}</p>
          </div>
        </div>
        
        <button className="w-full mt-4 py-2 bg-red-600 hover:bg-red-700 transition-all duration-300 rounded-lg text-white font-medium">
          Ver detalles y reservar
        </button>
      </div>
    </div>
  );
};

const ShowDetailsSection = ({ event, secciones, loadingSections, selectedSection, setSelectedSection, ticketQuantity, setTicketQuantity, onGoBack, formatCLP, calculateTotal, onPurchase, processingPurchase }) => {
  return (
    <section className="py-16 bg-black text-white">
      <div className="container mx-auto px-4">
        <button 
          onClick={onGoBack}
          className="flex items-center mb-8 text-white/80 hover:text-white transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Volver a la lista de eventos
        </button>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Columna izquierda - Información del show */}
          <div className="lg:col-span-2">
            <div className="bg-zinc-900 rounded-xl overflow-hidden mb-8">
              <div className="relative">
                <img 
                  src={event.image} 
                  alt={event.title} 
                  className="w-full h-64 object-cover" 
                />
                <div className="absolute top-0 right-0 m-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    event.type === 'bar' 
                      ? 'bg-red-600'
                      : 'bg-purple-600'
                  }`}>
                    {event.type === 'bar' ? 'Bar' : 'Teatro'}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <h2 className="text-3xl font-bold mb-2">{event.title}</h2>
                <p className="text-xl text-white/90 mb-4">{event.comedian}</p>
                
                <div className="bg-black/30 p-4 rounded-lg mb-6">
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <p className="text-white/70 text-sm">Fecha</p>
                      <p className="font-medium">{event.date}</p>
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Hora</p>
                      <p className="font-medium">{event.time} hrs.</p>
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Duración</p>
                      <p className="font-medium">{event.duration}</p>
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Lugar</p>
                      <p className="font-medium">{event.venue}, {event.location}</p>
                    </div>
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold mb-3">Acerca del evento</h3>
                <p className="text-white/80 mb-6">{event.description}</p>
                
                <div>
                  <h3 className="text-xl font-semibold mb-3">Comediantes</h3>
                  {event.comediantes.length === 1 ? (
                    <div className="flex items-start gap-4 mb-4">
                      <img 
                        src={event.comediantes[0].image} 
                        alt={event.comediantes[0].name} 
                        className="w-20 h-20 rounded-full object-cover" 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/80?text=Sin+Imagen';
                        }}
                      />
                      <div>
                        <h4 className="text-lg font-medium">{event.comediantes[0].name}</h4>
                        <p className="text-white/80">{event.comediantes[0].bio}</p>
                      </div>
                    </div>
                  ) : event.comediantes.length > 1 ? (
                    <>
                      {event.comediantes.filter(c => c.esPrincipal).map(comediante => (
                        <div key={comediante.name} className="flex items-start gap-4 mb-4">
                          <img 
                            src={comediante.image} 
                            alt={comediante.name} 
                            className="w-20 h-20 rounded-full object-cover" 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/80?text=Sin+Imagen';
                            }}
                          />
                          <div>
                            <h4 className="text-lg font-medium">{comediante.name}</h4>
                            <p className="text-white/80">{comediante.bio}</p>
                          </div>
                        </div>
                      ))}
                      
                      {event.comediantes.filter(c => !c.esPrincipal).length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          {event.comediantes.filter(c => !c.esPrincipal).map((comediante, index) => (
                            <div key={index} className="bg-zinc-800/50 p-3 rounded-lg flex flex-col items-center">
                              <img 
                                src={comediante.image} 
                                alt={comediante.name} 
                                className="w-16 h-16 rounded-full object-cover mb-2" 
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://via.placeholder.com/64?text=Sin+Imagen';
                                }}
                              />
                              <h5 className="font-medium">{comediante.name}</h5>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-white/80">Información de comediantes no disponible</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Información del lugar */}
            <div className="bg-zinc-900 rounded-xl overflow-hidden p-6">
              <h3 className="text-xl font-semibold mb-3">Sobre el lugar</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src={event.venueImage} 
                    alt={`Interior de ${event.venue}`} 
                    className="w-full h-48 object-cover" 
                  />
                </div>
                
                <div>
                  <h4 className="font-medium text-lg mb-2">{event.venue}</h4>
                  <p className="text-white/80 mb-3">
                    {event.type === 'bar' 
                      ? `Uno de los mejores bares de comedia en Santiago, ubicado en ${event.location}. Espacio íntimo con buena acústica.`
                      : `Reconocido teatro en ${event.location} con excelente acústica y visibilidad desde todos los asientos.`
                    }
                  </p>
                  <p className="text-white/80">Dirección: {event.venueAddress}</p>
                  <p className="text-white/80">Metro: {event.venueMetro}</p>
                  <p className="text-white/80 mt-3">
                    <span className="font-medium">Recomendaciones:</span> {' '}
                    {event.type === 'bar'
                      ? 'Llegar 30 minutos antes. Consumo mínimo no incluido en el precio de la entrada.'
                      : 'Se recomienda llegar con 45 minutos de anticipación. No está permitido el ingreso con alimentos.'
                    }
                  </p>
                </div>
              </div>
              
              <div className="mt-6">
                <h4 className="font-medium text-lg mb-2">Ubicación</h4>
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src={event.mapImage} 
                    alt={`Mapa de ${event.venue}`} 
                    className="w-full h-64 object-cover" 
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Columna derecha - Selección de boletos */}
          <div>
            <div className="bg-zinc-900 rounded-xl sticky top-6">
              <div className="p-6 border-b border-zinc-800">
                <h3 className="text-2xl font-bold mb-4">{event.type === 'bar' ? 'Reserva tu entrada' : 'Compra tu entrada'}</h3>
                
                {loadingSections ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-2 border-t-2 border-gray-200 border-t-red-600 rounded-full animate-spin mr-2"></div>
                    <span className="text-white/70">Cargando secciones...</span>
                  </div>
                ) : secciones.length > 0 ? (
                  <div className="space-y-4 mb-6">
                    {secciones.map((section) => (
                      <div 
                        key={section.id}
                        className={`p-4 rounded-lg cursor-pointer transition-colors duration-300 ${
                          selectedSection === section.id 
                            ? 'bg-purple-900/50 border border-purple-500' 
                            : 'bg-zinc-800 border border-transparent hover:border-purple-500/50'
                        } ${section.available <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => section.available > 0 && setSelectedSection(section.id)}
                      >
                        <div className="flex justify-between mb-1">
                          <div>
                            <h4 className="font-semibold">{section.name}</h4>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">{formatCLP(section.price)}</p>
                          </div>
                        </div>
                        <p className="text-white/70 text-sm">{section.details}</p>
                        <p className={`text-sm mt-1 ${section.available <= 0 ? 'text-red-400' : 'text-white/70'}`}>
                          {section.available <= 0 ? 'Agotado' : `${section.available} disponibles`}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-white/70">No hay secciones disponibles para este evento</p>
                  </div>
                )}
                
                {selectedSection && secciones.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-white/90 mb-2">Cantidad</label>
                    <div className="flex items-center bg-zinc-800 rounded-lg">
                      <button 
                        className="px-4 py-2 text-xl"
                        onClick={() => setTicketQuantity(Math.max(1, ticketQuantity - 1))}
                      >
                        -
                      </button>
                      <span className="flex-1 text-center text-lg">{ticketQuantity}</span>
                      <button 
                        className="px-4 py-2 text-xl"
                        onClick={() => {
                          const selectedSectionData = secciones.find(s => s.id === selectedSection);
                          const maxQuantity = Math.min(
                            selectedSectionData?.available || 1,
                            event.type === 'bar' ? 6 : 10
                          );
                          setTicketQuantity(Math.min(maxQuantity, ticketQuantity + 1));
                        }}
                      >
                        +
                      </button>
                    </div>
                    <p className="text-white/70 text-sm mt-1">
                      Máximo {event.type === 'bar' ? '6' : '10'} entradas por {event.type === 'bar' ? 'reserva' : 'compra'}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-white/90">Subtotal</span>
                  <span>{formatCLP(calculateTotal())}</span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-white/90">Cargo por servicio</span>
                  <span>{formatCLP(Math.round(calculateTotal() * 0.05))}</span>
                </div>
                <div className="flex justify-between items-center font-bold text-lg mb-6">
                  <span>Total</span>
                  <span>{formatCLP(calculateTotal() + Math.round(calculateTotal() * 0.05))}</span>
                </div>
                
                <button 
                  className={`w-full py-3 rounded-lg text-white font-medium transition-colors duration-300 ${
                    selectedSection && secciones.length > 0 && !processingPurchase
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-zinc-700 cursor-not-allowed'
                  }`}
                  disabled={!selectedSection || secciones.length === 0 || processingPurchase}
                  onClick={onPurchase}
                >
                  {processingPurchase ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-t-2 border-gray-200 border-t-white rounded-full animate-spin mr-2"></div>
                      Procesando compra...
                    </div>
                  ) : (
                    event.type === 'bar' ? 'Reservar ahora' : 'Comprar entradas'
                  )}
                </button>
                
                {event.type === 'bar' ? (
                  <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-white/90 text-sm">
                      <span className="font-medium">Nota:</span> El pago se realizará en el bar. Esta reserva garantiza tu lugar por hasta 30 minutos después de la hora de apertura.
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-white/90 text-sm">
                      <span className="font-medium">Nota:</span> Recibirás las entradas en tu correo electrónico tras completar la compra. Puedes mostrarlas impresas o en tu teléfono móvil.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VentaEntradas;